import { HsaClassificationCategoryStatus } from "@redotech/crdb-enums/conversion/hsa-classification-category-status";
import { HsaClassificationJobStatus } from "@redotech/crdb-enums/conversion/hsa-classification-job-status";
import { HsaClassificationReviewStatus } from "@redotech/crdb-enums/conversion/hsa-classification-review-status";
import { HsaGaleImportStatus } from "@redotech/crdb-enums/conversion/hsa-gale-import-status";
import { hsaEligibility } from "@redotech/redo-conversion-hsa-common/hsa-eligibility";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const hsaClassificationJob = z.object({
  id: z.string(),
  status: createEnum(HsaClassificationJobStatus),
  triggeredBy: z.string(),
  /** Families the classifier ran on this job; skipped and protected ones are not counted. */
  processedFamilies: z.number(),
  failedFamilies: z.number(),
  needsReviewCount: z.number(),
  maxFamilies: z.number().nullable(),
  jevCalls: z.number(),
  llmCalls: z.number(),
  estimatedCostUsd: z.number(),
  lastError: z.string().nullable(),
  startedAt: z.date().nullable(),
  finishedAt: z.date().nullable(),
  createdAt: z.date(),
});

const hsaGaleImport = z.object({
  status: createEnum(HsaGaleImportStatus),
  triggeredBy: z.string(),
  /** Null for a full run. */
  updatedSince: z.date().nullable(),
  rowsRead: z.number(),
  /** Gale rows whose Shopify variant is not in Redo's catalog. */
  rowsUnmatched: z.number(),
  lastError: z.string().nullable(),
  runStartedAt: z.date(),
  runFinishedAt: z.date().nullable(),
  /** When the first full import finished; null until then. */
  firstCompletedAt: z.date().nullable(),
});

const hsaClassificationSummary = z.object({
  decisionCounts: z.array(
    z.object({
      eligibility: hsaEligibility,
      reviewStatus: createEnum(HsaClassificationReviewStatus),
      count: z.number(),
    }),
  ),
  categoryCounts: z.array(
    z.object({
      status: createEnum(HsaClassificationCategoryStatus),
      count: z.number(),
    }),
  ),
  totalDecisions: z.number(),
  /** Classifiable families with no family decision. */
  unclassifiedFamilies: z.number(),
});

/**
 * The admin card's view of catalog classification. `forbidden` and
 * `engine_disabled` let the card explain itself instead of failing.
 */
export const getHsaClassificationSweepStatus = {
  name: "getHsaClassificationSweepStatus" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("forbidden") }),
    z.object({ kind: z.literal("engine_disabled") }),
    z.object({
      kind: z.literal("ok"),
      /** The latest job is queued or running. */
      sweepActive: z.boolean(),
      latestJob: hsaClassificationJob.nullable(),
      /** The team's import from Gale; null when it has never run. */
      galeImport: hsaGaleImport.nullable(),
      summary: hsaClassificationSummary,
      /** Variants in `redohsa.eligibility` as decided, waiting for a push, or whose last push failed. */
      metafieldPush: z
        .object({ pushed: z.number(), waiting: z.number(), failed: z.number() })
        .optional(),
    }),
  ]),
};

export type GetHsaClassificationSweepStatusOutput = z.infer<
  typeof getHsaClassificationSweepStatus.output
>;
